// SCROLL A TREE ROW INTO THE MIDDLE OF ITS CONTAINER, AND KEEP IT THERE.
//
// Owner, 2026-10-21: "why don't all these systems use the same viewer base",
// then "do both, fixes first then check on staging". The 3D engine has always
// been shared; this was not. Four pages each had their own idea of how to
// bring a clicked row into view, and between them they held every bug that
// took six rounds to find on the model viewer:
//
//   - scrolling to the library's slash KEY instead of the row's DOM id, so
//     getElementById found nothing and nothing moved (ModelViewer);
//   - scrollIntoView with behavior "smooth" over 100,000px, which is
//     asynchronous and unreliable at that distance, and block "nearest",
//     which is a no-op for a row already judged near (Viewer3d, Timeline);
//   - aiming once at a layout that changes in the next frame as the click's
//     render commits and lazy rows fill: measured 7,000px out, four times in
//     five (ModelViewer, before it converged);
//   - not scrolling at all (SocketViewer: the call was commented out).
//
// So: find the row, find the ancestor that ACTUALLY scrolls, position the
// container directly - instant and exact, so before/after mean what they say -
// then correct until the row is centred and STAYS centred for two frames.
// Bounded throughout, and it reports what it did rather than leaving "it
// didn't scroll" to be described over the phone.
//
// `doc` and `win` are injectable so the algorithm can be tested without a
// browser; everything else is the same code every page runs.

const CENTRED_PX = 4;      // close enough that another correction would jitter
const STABLE_FRAMES = 2;   // centred twice in a row = the layout has settled
const MAX_FIND_FRAMES = 20;
const MAX_FIX_FRAMES = 24;
const LATE_LOOK_MS = 400;

/** The nearest ancestor whose content overflows and whose overflow allows scrolling. */
export function scrollParentOf(el, win, doc) {
  let box = el && el.parentElement;
  while (box && box !== doc.body) {
    const ov = win.getComputedStyle(box).overflowY;
    if (box.scrollHeight > box.clientHeight + 1
        && (ov === "auto" || ov === "scroll" || ov === "overlay")) return box;
    box = box.parentElement;
  }
  return null;
}

/** How far the row is from centred, in pixels. Positive = below centre. */
export function offsetFromCentre(el, box) {
  const r = el.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  return (r.top - b.top) - (box.clientHeight / 2 - r.height / 2);
}

/**
 * Bring the row with DOM id `key` to the middle of its scroll container.
 *
 * opts.isAlive   - called before every step; return false and it stops (unmount).
 * opts.report    - called once with the outcome: {key, found, boxFound, before,
 *                  after, passes, offset, converged, boxH, scrollH, findFrames}.
 * Returns a cancel function.
 */
export function scrollRowToCentre(key, opts = {}) {
  const doc = opts.doc || (typeof document !== "undefined" ? document : null);
  const win = opts.win || (typeof window !== "undefined" ? window : null);
  const isAlive = opts.isAlive || (() => true);
  const report = opts.report || (() => {});
  if (!key || !doc || !win) return () => {};

  let raf = 0;
  let timer = 0;
  let cancelled = false;
  const raf_ = win.requestAnimationFrame
    ? (fn) => win.requestAnimationFrame(fn)
    : (fn) => win.setTimeout(fn, 16);

  // THE ROW MAY NOT EXIST YET: the branch it sits in is drawn by the render
  // this click triggers. A bounded wait, not a single look.
  let findFrames = 0;
  const find = () => {
    if (cancelled || !isAlive()) return;
    const el = doc.getElementById(key);
    if (!el) {
      findFrames += 1;
      if (findFrames > MAX_FIND_FRAMES) {
        report({
          key, found: false, findFrames,
          // A tree that drew nothing and a tree that drew the wrong branch are
          // different failures and look identical without this.
          rowsRendered: doc.querySelectorAll(".tree-item").length,
        });
        return;
      }
      raf = raf_(find);
      return;
    }
    fix(el);
  };

  const fix = (el) => {
    const box = scrollParentOf(el, win, doc);
    if (!box) { report({ key, found: true, boxFound: false, findFrames }); return; }
    const before = box.scrollTop;
    // CLAMPED IS FINISHED, NOT FAILED.
    //
    // A container with 40px of travel (boxH 950, scrollH 990) cannot put a row
    // in its middle - the arithmetic asks for a scrollTop the container will
    // never accept. The staging check of 2026-10-21 caught the loop burning
    // all 25 passes on exactly that and reporting converged:false, which reads
    // as a fault when the row was visible the whole time and the container was
    // already as close as it can get. So the goal is centring and the test is
    // what the person actually needs: the row in view, and no further travel
    // available in the direction we want.
    const apply = (want) => {
      const max = Math.max(0, box.scrollHeight - box.clientHeight);
      const next = Math.min(max, Math.max(0, want));
      const moved = next !== box.scrollTop;
      box.scrollTop = next;
      return moved;
    };
    apply(before + offsetFromCentre(el, box));

    let passes = 0;
    let stable = 0;
    let clamped = false;
    const converge = () => {
      if (cancelled || !isAlive()) return;
      const row = doc.getElementById(key);
      if (!row) { done(box, before, passes, NaN); return; }
      const off = offsetFromCentre(row, box);
      if (Math.abs(off) <= CENTRED_PX) {
        stable += 1;
        if (stable >= STABLE_FRAMES) { done(box, before, passes, off); return; }
      } else {
        stable = 0;
        // Nothing moved and nothing can: the container is at its limit, so
        // this IS the answer. Stop rather than spending the frame budget
        // asking for a position that does not exist.
        if (!apply(box.scrollTop + off)) { clamped = true; done(box, before, passes, off); return; }
      }
      passes += 1;
      if (passes > MAX_FIX_FRAMES) { done(box, before, passes, off); return; }
      raf = raf_(converge);
    };

    const done = (b, beforeTop, passes_, off) => {
      report({
        key, found: true, boxFound: true, findFrames,
        before: Math.round(beforeTop), after: Math.round(b.scrollTop),
        passes: passes_, offset: Number.isNaN(off) ? null : Math.round(off),
        // Centred, or as close as this container can ever get. Both mean the
        // person is looking at the row; `clamped` says which happened.
        converged: Math.abs(off) <= CENTRED_PX || clamped,
        clamped,
        boxH: b.clientHeight, scrollH: b.scrollHeight,
      });
    };

    // The reflow a frame loop cannot see: a lazy row answering the network
    // after we have stopped looking.
    timer = win.setTimeout(() => {
      if (cancelled || !isAlive()) return;
      const row = doc.getElementById(key);
      if (!row || !box.isConnected) return;
      const off = offsetFromCentre(row, box);
      if (Math.abs(off) > CENTRED_PX) box.scrollTop = Math.max(0, box.scrollTop + off);
    }, LATE_LOOK_MS);

    raf = raf_(converge);
  };

  find();
  return () => {
    cancelled = true;
    if (raf && win.cancelAnimationFrame) win.cancelAnimationFrame(raf);
    if (timer) win.clearTimeout(timer);
  };
}
